/**
 * Color-axis stock heatmap (TradingView-style).
 * Mono-size tiles colored by day change % + legend scale. Not a scanner.
 */

type HmItem = {
  symbol: string;
  label: string;
  ltp: number | null;
  chg: number | null;
  chgPct: number | null;
  logoUrl?: string | null;
};

type Deps = {
  watchlistUrl: string;
  openSymbol: (symbol: string) => void;
  getQuotesBag?: () => Record<string, any>;
};

const UNIVERSES = [
  { id: "nifty50", label: "Nifty 50" },
  { id: "nifty500", label: "Nifty 500" },
  { id: "fno", label: "F&O stocks" },
  { id: "commodity", label: "Commodities" },
  { id: "indices", label: "Indices" },
  { id: "sectors", label: "Sector indices" },
] as const;

const UNIVERSE_IDS: readonly string[] = UNIVERSES.map((u) => u.id);

/** Symmetric color axis cutoff (±%). Beyond this = full intensity. */
const DEFAULT_CUTOFF = 3;

function chgPctOf(q: any): number | null {
  if (!q) return null;
  if (q.chg_pct != null && isFinite(Number(q.chg_pct))) return Number(q.chg_pct);
  const ltp = Number(q.ltp);
  const prev = Number(q.prev_close);
  const chg = Number(q.chg);
  if (ltp > 0 && prev > 0) return ((ltp - prev) / prev) * 100;
  if (ltp > 0 && isFinite(chg) && prev > 0) return (chg / prev) * 100;
  return null;
}

function colorFor(pct: number | null, cutoff: number): string {
  if (pct == null || !isFinite(pct)) return "rgb(72, 78, 84)";
  // StockMojo-style: two poles only — never blend through grey/yellow.
  // Soft pale green/pink near 0 → deep forest green / crimson at ±cutoff.
  const t = Math.max(-1, Math.min(1, pct / Math.max(cutoff, 0.25)));
  const u = Math.abs(t);
  // Ease so small moves stay readable (not washed white)
  const e = Math.pow(u, 0.72);

  if (t >= 0) {
    // +0.07% pale mint → +3.8% deep forest
    const soft = { r: 110, g: 168, b: 112 }; // still clearly green
    const deep = { r: 22, g: 110, b: 48 }; // forest
    const r = Math.round(soft.r + (deep.r - soft.r) * e);
    const g = Math.round(soft.g + (deep.g - soft.g) * e);
    const b = Math.round(soft.b + (deep.b - soft.b) * e);
    return "rgb(" + r + "," + g + "," + b + ")";
  }
  // -0.01% soft rose → -4.9% deep crimson
  const soft = { r: 214, g: 120, b: 118 };
  const deep = { r: 168, g: 28, b: 28 };
  const r = Math.round(soft.r + (deep.r - soft.r) * e);
  const g = Math.round(soft.g + (deep.g - soft.g) * e);
  const b = Math.round(soft.b + (deep.b - soft.b) * e);
  return "rgb(" + r + "," + g + "," + b + ")";
}

function textTone(pct: number | null): string {
  if (pct == null) return "flat";
  if (pct > 0.05) return "up";
  if (pct < -0.05) return "down";
  return "flat";
}

export function initHeatmap(deps: Deps): {
  open: (universe?: string) => void;
  close: () => void;
} {
  const root = document.getElementById("cxHeatmap");
  const grid = document.getElementById("cxHeatmapGrid");
  const meta = document.getElementById("cxHeatmapMeta");
  const uniSel = document.getElementById("cxHeatmapUniverse") as HTMLSelectElement | null;
  const cutoffSel = document.getElementById("cxHeatmapCutoff") as HTMLSelectElement | null;

  let items: HmItem[] = [];
  let pollTimer: number | null = null;
  let universe = "nifty50";
  let cutoff = DEFAULT_CUTOFF;

  /** Gainers first (green left), then losers — no UI toggle. */
  function sortItems(): void {
    items.sort(function (a, b) {
      const ap = a.chgPct;
      const bp = b.chgPct;
      if (ap == null && bp == null) return 0;
      if (ap == null) return 1;
      if (bp == null) return -1;
      return bp - ap;
    });
  }

  if (uniSel && !uniSel.options.length) {
    UNIVERSES.forEach(function (u) {
      const o = document.createElement("option");
      o.value = u.id;
      o.textContent = u.label;
      uniSel.appendChild(o);
    });
  }

  function renderGrid(): void {
    if (!grid) return;
    if (!items.length) {
      grid.innerHTML = '<div class="cx-hm-empty">No symbols in this universe yet.</div>';
      return;
    }
    grid.innerHTML = items
      .map(function (it) {
        const pct = it.chgPct;
        const pctStr =
          pct == null ? "—" : (pct >= 0 ? "+" : "") + pct.toFixed(2) + "%";
        const px =
          it.ltp != null && it.ltp > 0
            ? it.ltp.toLocaleString("en-IN", { maximumFractionDigits: 2 })
            : "—";
        const logo = it.logoUrl
          ? '<img class="cx-hm-logo" src="' +
            escapeAttr(it.logoUrl) +
            '" alt="" loading="lazy" decoding="async" />'
          : "";
        return (
          '<button type="button" class="cx-hm-tile tone-' +
          textTone(pct) +
          '" data-symbol="' +
          escapeAttr(it.symbol) +
          '" style="background:' +
          colorFor(pct, cutoff) +
          '" title="' +
          escapeAttr(it.symbol) +
          " · " +
          pctStr +
          '">' +
          '<span class="cx-hm-head">' +
          logo +
          '<span class="cx-hm-sym">' +
          escapeHtml(it.label) +
          "</span>" +
          "</span>" +
          '<span class="cx-hm-pct">' +
          pctStr +
          "</span>" +
          '<span class="cx-hm-px">' +
          px +
          "</span>" +
          "</button>"
        );
      })
      .join("");
    if (meta) {
      meta.textContent = items.length + " symbols · ±" + cutoff + "% intensity";
    }
  }

  async function load(): Promise<void> {
    try {
      const res = await fetch(deps.watchlistUrl + "?_=" + Date.now(), {
        credentials: "same-origin",
        cache: "no-store",
      });
      const data = await res.json();
      const bag = deps.getQuotesBag ? deps.getQuotesBag() : {};
      const rows: any[] =
        UNIVERSE_IDS.includes(universe) && Array.isArray(data[universe]) ? data[universe] : [];

      items = (rows || [])
        .map(function (r: any) {
          const label = String(r.label || r.symbol || "");
          const symbol = String(r.symbol || r.label || "");
          const display = universe === "commodity" ? symbol || label : label || symbol;
          const live = bag[label] || bag[symbol] || bag[String(label).toUpperCase()];
          const q = live || r;
          let pct = null;
          if (r.chg_pct != null && isFinite(Number(r.chg_pct))) pct = Number(r.chg_pct);
          else if (r.chg != null && isFinite(Number(r.chg))) pct = Number(r.chg);
          else pct = chgPctOf(q);
          const ltp = Number(q.ltp || r.ltp) || null;
          return {
            symbol: label || symbol,
            label: display,
            ltp,
            chg: q.chg != null ? Number(q.chg) : null,
            chgPct: pct,
            logoUrl: r.logo_url || null,
          } as HmItem;
        })
        .filter((x: HmItem) => !!x.symbol);
      sortItems();
      renderGrid();
    } catch (e) {
      if (grid) {
        grid.innerHTML =
          '<div class="cx-hm-empty">Failed to load heatmap data.</div>';
      }
    }
  }

  function open(uni?: string): void {
    if (!root) return;
    if (uni) universe = uni;
    if (uniSel) uniSel.value = universe;
    root.classList.add("is-open");
    root.setAttribute("aria-hidden", "false");
    document.body.classList.add("cx-heatmap-open");
    load();
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = window.setInterval(load, 5000);
  }

  function close(): void {
    if (!root) return;
    root.classList.remove("is-open");
    root.setAttribute("aria-hidden", "true");
    document.body.classList.remove("cx-heatmap-open");
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  document.getElementById("cxHeatmapBtn")?.addEventListener("click", function () {
    open();
  });
  document.getElementById("cxMobileHeatmapBtn")?.addEventListener("click", function () {
    open();
  });
  document.getElementById("cxHeatmapClose")?.addEventListener("click", close);
  uniSel?.addEventListener("change", function () {
    universe = uniSel.value || "nifty50";
    load();
  });
  cutoffSel?.addEventListener("change", function () {
    cutoff = Number(cutoffSel.value) || DEFAULT_CUTOFF;
    renderGrid();
  });
  grid?.addEventListener("click", function (e) {
    const tile = (e.target as HTMLElement).closest(".cx-hm-tile") as HTMLElement | null;
    if (!tile) return;
    const sym = tile.getAttribute("data-symbol") || "";
    if (!sym) return;
    close();
    deps.openSymbol(sym);
  });
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && root?.classList.contains("is-open")) close();
  });

  return { open, close };
}

function escapeHtml(s: string): string {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, "&quot;");
}
